import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { changePassword } from '../../store/session';
import { FaSpinner } from 'react-icons/fa';

function ChangePasswordModal({ onClose }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const sessionUser = useSelector(state => state.session.user);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Password requirement states
  const [requirements, setRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
    matching: false
  });

  // Update requirements as user types
  useEffect(() => {
    setRequirements({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
      matching: password === confirmPassword && password.length > 0
    });
  }, [password, confirmPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);

    // Check if all requirements are met
    const allRequirementsMet = Object.values(requirements).every(req => req);

    if (!allRequirementsMet) {
      const strengthErrors = [];
      if (!requirements.length) strengthErrors.push('Password must be at least 8 characters long');
      if (!requirements.uppercase) strengthErrors.push('Password must contain at least one uppercase letter');
      if (!requirements.lowercase) strengthErrors.push('Password must contain at least one lowercase letter');
      if (!requirements.number) strengthErrors.push('Password must contain at least one number');
      if (!requirements.special) strengthErrors.push('Password must contain at least one special character');
      if (!requirements.matching) strengthErrors.push('Passwords do not match');

      setErrors(strengthErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await dispatch(changePassword(password));

      if (result) {
        // If there are errors
        setErrors(result);
        setIsSubmitting(false);
        return;
      }

      // Password changed successfully
      if (onClose) {
        onClose();
      }

      // Redirect based on user role
      if (sessionUser && sessionUser.is_admin) {
        // Redirect admin users to admin dashboard
        history.push('/admin/dashboard');
      } else {
        // Redirect non-admin users to home page
        history.push('/');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setErrors(['An unexpected error occurred. Please try again.']);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg p-8 w-11/12 max-w-2xl shadow-md text-lg">
        <h2 className="mt-0 text-blue-600 text-3xl font-bold mb-4">First Time Login - Change Your Password</h2>
        <p className="mb-2 text-gray-600 leading-normal text-lg">
          For security reasons, you need to change your password on first login.
          Your new password must meet the following requirements:
        </p>

        <ul className="mb-6 text-gray-600 list-none p-0">
          <li className={`mb-2 text-base flex items-center transition-colors ${requirements.length ? 'text-green-600' : 'text-red-600'}`}>
            <span className="inline-block w-5 mr-2 font-bold text-center">{requirements.length ? '✓' : '✗'}</span>
            Be at least 8 characters long
          </li>
          <li className={`mb-2 text-base flex items-center transition-colors ${requirements.uppercase ? 'text-green-600' : 'text-red-600'}`}>
            <span className="inline-block w-5 mr-2 font-bold text-center">{requirements.uppercase ? '✓' : '✗'}</span>
            Include at least one uppercase letter
          </li>
          <li className={`mb-2 text-base flex items-center transition-colors ${requirements.lowercase ? 'text-green-600' : 'text-red-600'}`}>
            <span className="inline-block w-5 mr-2 font-bold text-center">{requirements.lowercase ? '✓' : '✗'}</span>
            Include at least one lowercase letter
          </li>
          <li className={`mb-2 text-base flex items-center transition-colors ${requirements.number ? 'text-green-600' : 'text-red-600'}`}>
            <span className="inline-block w-5 mr-2 font-bold text-center">{requirements.number ? '✓' : '✗'}</span>
            Include at least one number
          </li>
          <li className={`mb-2 text-base flex items-center transition-colors ${requirements.special ? 'text-green-600' : 'text-red-600'}`}>
            <span className="inline-block w-5 mr-2 font-bold text-center">{requirements.special ? '✓' : '✗'}</span>
            Include at least one special character
          </li>
          <li className={`mb-2 text-base flex items-center transition-colors ${requirements.matching ? 'text-green-600' : 'text-red-600'}`}>
            <span className="inline-block w-5 mr-2 font-bold text-center">{requirements.matching ? '✓' : '✗'}</span>
            Passwords match
          </li>
        </ul>

        {errors.length > 0 && (
          <div className="bg-red-100 rounded p-3 mb-4">
            {errors.map((error, idx) => (
              <p key={idx} className="text-red-700 m-1 text-base">{error}</p>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label htmlFor="password" className="block mb-2 font-medium text-gray-700 text-lg">New Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isSubmitting}
              className="w-full p-3 border border-gray-300 rounded text-lg focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600 focus:ring-opacity-20"
            />
          </div>

          <div className="mb-6">
            <label htmlFor="confirmPassword" className="block mb-2 font-medium text-gray-700 text-lg">Confirm Password</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={isSubmitting}
              className="w-full p-3 border border-gray-300 rounded text-lg focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600 focus:ring-opacity-20"
            />
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              className={`bg-blue-600 text-white border-none rounded px-6 py-3 text-lg font-medium cursor-pointer transition-colors ${isSubmitting || !Object.values(requirements).every(req => req) ? 'bg-blue-300 cursor-not-allowed' : 'hover:bg-blue-700'}`}
              disabled={isSubmitting || !Object.values(requirements).every(req => req)}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <FaSpinner className="animate-spin mr-2" />
                  Changing Password...
                </span>
              ) : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ChangePasswordModal;
